import { Component, OnInit, Output, EventEmitter, Inject, ViewChild, ChangeDetectionStrategy, ViewEncapsulation, Input, ChangeDetectorRef, SimpleChanges, Injectable, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { OneQuickSelectionDatepickerService } from '@one/angular-kit/form';
import { ReminderDatePickerComponent } from './reminder-date-picker/reminder-date-picker.component';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatRadioChange } from '@angular/material/radio';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OneSnackBarService, OneSnackBarType } from '@one/angular-kit/modal';
import { CreateReminderDialogService } from '../create-reminder-dialog.service';
import { Subject } from 'rxjs';
import { NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { formatDate } from '@angular/common';
import { toHTML } from 'ngx-editor';
import { QnSbnDataService } from '../../../services/qn-sbn-data-service.service';
import { QnSbnService } from '../../../services/qn-sbn-service.service';
import { HttpErrorResponse } from '@angular/common/http';

export const PICK_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

@Injectable()
class PickDateAdapter extends NativeDateAdapter {
  public format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      return formatDate(date, 'dd-MMM-yyyy', this.locale);
    } else {
      return date.toDateString();
    }
  }
}

@Component({
  selector: 'dl-create-reminder',
  templateUrl: './create-reminder.component.html',
  styleUrls: ['./create-reminder.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: PickDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS }
  ]
})

export class CreateReminderComponent implements OnInit, AfterViewInit {
  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: any, 
    private qnsbnservice :QnSbnService, 
    private _QnSbnDataService:QnSbnDataService,
    public dialogRef: MatDialogRef<CreateReminderComponent>, 
    private createReminderDialogService: CreateReminderDialogService, 
    private changeDetectorRef: ChangeDetectorRef, 
    private readonly quickSelectionService: OneQuickSelectionDatepickerService, 
    private readonly oneSnackBar?: OneSnackBarService
  ) {
    quickSelectionService.setDate(this.reminderDate);
    quickSelectionService.setDateShortcut(this.dateShortcut);
    this.isDialogMode = !!(data && data.isDialogMode);
    if (data && data.notificationData) {
      this.notificationData = data.notificationData;
    }
    this.templateSeed = data?.templateSeed;
    if (this.isDialogMode && Array.isArray(data?.remindersData)) {
      this.remindersData = data.remindersData;
    }
  }

  @ViewChild('reminderDatepicker', { static: true }) public matDatepicker: MatDatepicker<any>;
  @Input() public reminderRTEDoc;
  public feedbackDueDate = '';
  public isEditRow = false;
  public prevReminderData = '';
  public reminderDate = new FormControl('');
  public dateShortcut: FormControl = new FormControl('');
  public reminderConsent: FormControl = new FormControl('');
  public isReminderFormComplete = false;
  public subjectName = new FormControl();
  public addTempleteCheck = false;
  public remindersData = this.createReminderDialogService.getRemindersDate() || [];
  public selectedConcent:any;
  public isTemplateDisabled:boolean;
  public datepickerHeader = ReminderDatePickerComponent;
  public templateData: any;
  public createReminderTxt: any;
  public templateSubject: string = '';
  public templateEmailText: string = '';
  public templateId: string = '';
  public templateColumn = ['dragHandle', 'selectText', 'messageTemplate', 'iconSets'];
  public disabledApplyCheckbox = true;
  public notificationData: any;
  public keyUpForRTE = false;
  public keyUpForSubjectName = false;
  private readonly isDialogMode: boolean;
  private readonly templateSeed: any;

public reminderDateFilter = (d: Date | null): boolean => {
  const startDate = d || new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  
  // Reminder date must be greater than today
  if (startDate <= today) {
    return false;
  }
  
  // If feedback due date is set, reminder date must be less than due date
  if (this.data.feedbackDueDate) {
    const dueDate = new Date(this.data.feedbackDueDate);
    dueDate.setHours(0, 0, 0, 0);
    if (startDate >= dueDate) {
      return false;
    }
  }
  
  // Check for date conflicts with other reminders
  const currentRemindersData = this.data.remindersData || this.remindersData;
  const reminderDates = currentRemindersData.map(reminder => reminder['reminderDate']);
  if (this.data.isEdit) {
    return !reminderDates.find(x => {
      if (!x) return false;
      const existingDate = new Date(x);
      existingDate.setHours(0, 0, 0, 0);
      const currentReminderDate = new Date(this.data.reminderData.reminderDate);
      currentReminderDate.setHours(0, 0, 0, 0);
      return existingDate.getTime() === startDate.getTime() && existingDate.getTime() !== currentReminderDate.getTime();
    });
  } else {
    return !reminderDates.find(x => {
      if (!x) return false;
      const existingDate = new Date(x);
      existingDate.setHours(0, 0, 0, 0);
      return existingDate.getTime() === startDate.getTime();
    });
  }
}

public toggleApplyButton(evt: boolean): void {
  this.addTempleteCheck = evt;
}

public setDateFilter(value: number): void {
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + value);
  this.reminderDate.setValue(nextDate);
}

public formatDate(value: Date): string {
  const date = new Date(value);
  const day = date.toLocaleString('en-US', { day: '2-digit' });
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.toLocaleString('en-US', { year: 'numeric' });
  return day + '-' + month + '-' + year;
}

public ngOnInit(): void {
  this.isTemplateDisabled = !this._QnSbnDataService.template;
  this.quickSelectionService.isCloseCalendar.subscribe(isClose => {
    if (isClose) {
      this.matDatepicker.close();
    }
  });
  this.feedbackDueDate = this.data.feedbackDueDate ? this.formatDate(this.data.feedbackDueDate) : '';
  this.subjectName.valueChanges.subscribe(x => {
    this.getApplybuttonStatus();
  })
  this._QnSbnDataService.mappingData$.subscribe(observer => {
    if (!this._QnSbnDataService.isEmpty(observer['localNotificationId'])) {
      this.notificationData = observer['localNotificationId'];
    }
    if (this.isDialogMode) {
      return;
    }
    if (!this._QnSbnDataService.isEmpty(observer['reminderAttachment'])) {
      this.templateSubject = observer['reminderAttachment']['reminderSubjectName'];
      this.templateEmailText = observer['reminderAttachment']['reminderAttachmentRTEText'];
      this.templateId = observer['reminderAttachment']['templateId'];
      this.subjectName.setValue(this.templateSubject);
      this.reminderRTEDoc = this.templateEmailText;
    }
  });

  if (this.templateSeed) {
    this.applyTemplateSeed(this.templateSeed);
  }
}

public ngAfterViewInit(){
  if (this.isEditRow === false && this.data.isBlank === true) {
    this.subjectName.setValue('');
    this.reminderRTEDoc = '<p></p>';
    this.createReminderTxt = '<p></p>';
    this.reminderConsent.setValue('');
    this.reminderDate.setValue('');
  }
  this.isEditRow = this.data.isEdit;
  this.prevReminderData = this.data.reminderData;
  const dueDateValue = this.data.feedbackDueDate ? new Date(this.data.feedbackDueDate) : null;
  if (!this.isDialogMode && dueDateValue) {
    this.createReminderDialogService.setFeedbackDueDate(dueDateValue);
  }
  if (this.isEditRow === true) {
    this.subjectName.setValue(this.data.reminderData.reminderSubject);
    this.reminderDate.setValue(new Date(this.data.reminderData.reminderDate));
    this.reminderRTEDoc = this.data.reminderData.reminderMessage;
    this.reminderConsent.setValue(this.data.reminderData.reminderSendMode);
    this.createReminderTxt = this.data.reminderData.reminderMessage;
    this.changeDetectorRef.detectChanges();
  }
}

public addReminder(): void {
  // Validate reminder date before adding
  if (!this.reminderDate.value) {
    this.oneSnackBar.open('Please select a reminder date', OneSnackBarType.WARNING, '', { duration: 3000 });
    return;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reminderDate = new Date(this.reminderDate.value);
  reminderDate.setHours(0, 0, 0, 0);

  if (reminderDate <= today) {
    this.oneSnackBar.open('Reminder date must be greater than today', OneSnackBarType.WARNING, '', { duration: 3000 });
    return;
  }

  if (this.data.feedbackDueDate) {
    const dueDate = new Date(this.data.feedbackDueDate);
    dueDate.setHours(0, 0, 0, 0);
    if (reminderDate >= dueDate) {
      this.oneSnackBar.open('Reminder date must be less than the feedback due date', OneSnackBarType.WARNING, '', { duration: 3000 });
      return;
    }
  }

  const reminderData = [
    {
      reminderDate: this.formatDate(this.reminderDate.value),
      reminderSubject: this.subjectName.value,
      reminderMessage: this.createReminderTxt || this.reminderRTEDoc,
      reminderSendMode: this.reminderConsent.value,
      reminderActionIcons: true,
      reminderAddTemplate: this.addTempleteCheck ? 'true' : '',
      reminderSendAutomatically: true
    }
  ];

  this.dialogRef.close({ 
    data: reminderData,
    shouldCreateTemplate: this.addTempleteCheck
  });
  
  // Handle template creation for both dialog and non-dialog modes
  if (this.addTempleteCheck) {
    setTimeout(() => {
      this.createTemplateUsingApply();
      if (!this.data.isDialogMode) {
        this.oneSnackBar.open('Reminder successfully added to notification and templates', OneSnackBarType.SUCCESS, '', { duration: 3000 });
      }
    }, 100);
  } else if (!this.data.isDialogMode) {
    setTimeout(() => {
      this.oneSnackBar.open('Reminder successfully added to notification', OneSnackBarType.SUCCESS, '', { duration: 3000 });
    }, 100);
  }
}

public updateReminder(): void {
  // Validate reminder date before updating
  if (!this.reminderDate.value) {
    this.oneSnackBar.open('Please select a reminder date', OneSnackBarType.WARNING, '', { duration: 3000 });
    return;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reminderDate = new Date(this.reminderDate.value);
  reminderDate.setHours(0, 0, 0, 0);

  if (reminderDate <= today) {
    this.oneSnackBar.open('Reminder date must be greater than today', OneSnackBarType.WARNING, '', { duration: 3000 });
    return;
  }

  if (this.data.feedbackDueDate) {
    const dueDate = new Date(this.data.feedbackDueDate);
    dueDate.setHours(0, 0, 0, 0);
    if (reminderDate >= dueDate) {
      this.oneSnackBar.open('Reminder date must be less than the feedback due date', OneSnackBarType.WARNING, '', { duration: 3000 });
      return;
    }
  }

  const reminderData = [
    {
      reminderDate:  this.formatDate(this.reminderDate.value),
      reminderSubject: this.subjectName.value,
      reminderMessage: this.createReminderTxt || this.reminderRTEDoc,
      reminderSendMode: this.reminderConsent.value,
      reminderActionIcons: true,
      reminderAddTemplate: this.addTempleteCheck ? 'true' : '',
      reminderSendAutomatically: true
    }
  ];
  
  this.dialogRef.close({ 
    data: reminderData,
    shouldCreateTemplate: this.addTempleteCheck
  });
  
  // Handle template creation for both dialog and non-dialog modes
  if (this.addTempleteCheck) {
    setTimeout(() => {
      this.createTemplateUsingApply();
    }, 100);
  }
  
  if (!this.data.isDialogMode) {
    this.oneSnackBar.open('Reminder successfully updated to notification', OneSnackBarType.SUCCESS, '', { duration: 3000 });
  }
}

public getReminderDate(rdd: Date): void {
  this.isTemplateDisabled = !this.isTemplateDisabled;
}

public radioChange($event: MatRadioChange) {}

public onKeyupSubjectName(evt) {
  evt.preventDefault();
  this.keyUpForSubjectName = true;
}

public onBlurSubjectName(evt) {
  this.subjectName.setValue(this.subjectName.value.trim());
  this.addProduct(this.subjectName.value,this.reminderRTEDoc);
}

public createTemplateUsingApply(): void {
  const newTemplateData = {
    "templateSubject": this.subjectName.value,
    "templateBody": this.reminderRTEDoc,
    "notificationType": this.notificationData.Type,
    "messageType": "Reminder",
    "templateLanguage": this.notificationData.LocalNotificationLanguage
  };
  
  this.qnsbnservice.createUpdateMessageTemplate(newTemplateData).subscribe(
    (responseData:any) => {
      this.addProduct(responseData.templateSubject,responseData.templateBody);
      this.addTempleteCheck = false;
      this.disabledApplyCheckbox = true;
      this.changeDetectorRef.detectChanges();
    },
    (error: HttpErrorResponse) => {
      // console.log('error:', error);
      this.qnsbnservice.httpErrorHandler(error, 'Could not save reminder template.');
    }
  );
}

public setRTEText(evt): void {
  this.createReminderTxt = toHTML(evt);
  const newText = toHTML(evt);
  if (this.reminderRTEDoc === newText) {
    return;
  } else {
    this.reminderRTEDoc = newText;
    this.getApplybuttonStatus();
    this.addProduct(this.subjectName.value,this.reminderRTEDoc);
  }
}

public getApplybuttonStatus(): void {
  if (this.subjectName.value !== '' && this.reminderRTEDoc && this.reminderRTEDoc !== '<p></p>') {
    this.disabledApplyCheckbox = false;
  } else {
    this.disabledApplyCheckbox = true;
  }
}

public addProduct(subjectName, docRTEtext) {    
  if (this.isDialogMode) {
    return;
  }
  this._QnSbnDataService.getMappedQnsbnData({
    "reminderSubjectName": subjectName,
    "reminderAttachmentRTEText": docRTEtext,
    "templateId": this.templateId || ""
  },"reminderAttachment");
}

ngOnDestroy(): void {
  this.addProduct(this.subjectName.value,this.reminderRTEDoc);
}

private applyTemplateSeed(seed: any): void {
  if (!seed) {
    return;
  }

  const templateDetails = seed.templateDetails || seed;
  const subject = templateDetails?.templateSubject || templateDetails?.subject || '';
  const message = templateDetails?.templateBody || templateDetails?.messageTemplate || '';

  this.templateId = seed.templateId || templateDetails?.templateId || '';
  if (subject) {
    this.subjectName.setValue(subject);
  }
  if (message) {
    this.reminderRTEDoc = message;
    this.createReminderTxt = message;
  }
  this.getApplybuttonStatus();
  this.changeDetectorRef.detectChanges();
}
}
